import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Trash2, Check } from "lucide-react";
import { Chip } from "@mui/material";
import AvatarImage from "./AvatarImage";

export interface UserOption {
  id: string;
  name: string;
  emailAddress: string;
  avatarUri?: string | null;
}

export interface UserSelectorProps {
  label: string;
  userOptions: UserOption[];
  selectedUserIds: string[];
  onSelectionChange: (userIds: string[]) => void;
  placeholder?: string;
  width?: string;
  dropdownWidth?: string;
  labelClassName?: string;
  disabled?: boolean;
}

export default function UserSelector({
  label,
  userOptions,
  selectedUserIds,
  onSelectionChange,
  placeholder = "Select users",
  width = "w-64",
  dropdownWidth = "w-80",
  labelClassName = "text-gray-300",
  disabled = false
}: UserSelectorProps) {
  const [usersOpen, setUsersOpen] = useState(false);
  const [userSearchText, setUserSearchText] = useState('');
  const usersRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (usersRef.current && !usersRef.current.contains(e.target as Node)) {
        setUsersOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const toggleUser = (userId: string) => {
    const newSelection = selectedUserIds.includes(userId)
      ? selectedUserIds.filter(id => id !== userId)
      : [...selectedUserIds, userId];
    onSelectionChange(newSelection);
  };

  // Filter users based on search text
  const filteredUserOptions = userOptions.filter(u =>
    userSearchText.trim() === '' ||
    u.name.toLowerCase().includes(userSearchText.toLowerCase()) ||
    u.emailAddress.toLowerCase().includes(userSearchText.toLowerCase())
  );

  return (
    <div className="flex flex-col gap-1 relative" ref={usersRef}>
      <label className={`text-sm ${labelClassName}`}>{label}</label>
      <div
        onClick={() => !disabled && setUsersOpen(o => !o)}
        className={`${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-text'} bg-white text-gray-900 min-h-[40px] ${width} rounded text-sm flex items-center gap-1 flex-nowrap whitespace-nowrap border border-gray-300 px-2 py-1 overflow-x-auto overflow-y-hidden`}
      >
        {selectedUserIds.length === 0 && (
          <span className="text-gray-500">{placeholder}</span>
        )}
        {selectedUserIds.map(userId => {
          const user = userOptions.find(u => u.id === userId);
          const displayName = user?.name || user?.emailAddress || userId;
          return (
            <Chip
              key={userId}
              variant="outlined"
              size="small"
              label={displayName}
              onDelete={(e) => { e.stopPropagation?.(); toggleUser(userId); }}
              avatar={<AvatarImage name={user?.name || displayName} filename={user?.avatarUri || null} size={18} />}
              sx={{ mr: 0.5, '& .MuiChip-deleteIcon': { color: '#1D5176' }, flexShrink: 0 }}
              onMouseDown={(e) => e.stopPropagation()}
            />
          );
        })}
        <ChevronDown className={`ml-auto h-4 w-4 text-gray-500 ${usersOpen ? 'rotate-180' : ''}`} />
      </div>

      {usersOpen && (
        <div className={`absolute top-full left-0 mt-1 bg-white text-gray-900 rounded shadow-lg p-2 z-50 border border-gray-200 ${dropdownWidth}`}>
          <input
            type="text"
            placeholder="Search users..."
            value={userSearchText}
            onChange={(e) => setUserSearchText(e.target.value)}
            className="w-full px-2 py-1 mb-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="max-h-56 overflow-auto pr-1">
            {filteredUserOptions.map(u => (
              <label key={u.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
                <AvatarImage name={u.name} filename={u.avatarUri || null} size={20} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate font-medium text-left" title={u.name}>{u.name}</div>
                  <div className="text-xs text-gray-500 truncate text-left" title={u.emailAddress}>{u.emailAddress}</div>
                </div>
                <input
                  checked={selectedUserIds.includes(u.id)}
                  onChange={() => toggleUser(u.id)}
                  type="checkbox"
                  className="h-4 w-4 flex-shrink-0"
                />
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between mt-2">
            <button
              onClick={() => onSelectionChange([])}
              className="text-xs text-gray-600 hover:text-gray-800 flex items-center gap-1"
            >
              <Trash2 className="h-3.5 w-3.5"/>Clear
            </button>
            <button
              onClick={() => setUsersOpen(false)}
              className="text-xs bg-gray-900 text-white px-2 py-1 rounded flex items-center gap-1"
            >
              <Check className="h-3.5 w-3.5"/>Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
